import type { ExtendedBaggage, PassengerWithBaggage } from '../types';

const getSelectedBaggagePrice = (baggage: ExtendedBaggage[]) => {
  return Number(baggage.find(item => item.isSelected)?.price.amount) || 0;
};

const getSelectedBaggageOriginalPrice = (baggage: ExtendedBaggage[]) => {
  const selectedBaggage = baggage.find(item => item.isSelected);

  if (!selectedBaggage) {
    return 0;
  }

  if (hasBaggageDiscount(selectedBaggage)) {
    const originalPrice = Number(getBaggageOriginalPrice(selectedBaggage));

    if (!Number.isNaN(originalPrice)) {
      return originalPrice;
    }
  }

  return Number(selectedBaggage.price.amount) || 0;
};

const getTotalPrice = (passengers: PassengerWithBaggage[]) => {
  return passengers.reduce((sum, passenger) => {
    const totalPricesByFlights = passenger.flightsWithBaggages.map(flight =>
      getSelectedBaggagePrice(flight.availableBaggages),
    );

    const flightSum = totalPricesByFlights.reduce(
      (sum, flight) => sum + flight,
      0,
    );
    return sum + flightSum;
  }, 0);
};

const getTotalOriginalPrice = (passengers: PassengerWithBaggage[]) => {
  return passengers.reduce((sum, passenger) => {
    const totalOriginalPricesByFlights = passenger.flightsWithBaggages.map(
      flight => getSelectedBaggageOriginalPrice(flight.availableBaggages),
    );

    const flightSum = totalOriginalPricesByFlights.reduce(
      (sum, flight) => sum + flight,
      0,
    );
    return sum + flightSum;
  }, 0);
};

const getBaggageMinPrice = (
  baggages: ExtendedBaggage[],
): string | undefined => {
  return baggages.reduce(
    (minAmountStr, currentItem) => {
      const currentAmount = Number.parseFloat(currentItem?.price?.amount);

      if (Number.isNaN(currentAmount)) {
        return minAmountStr;
      }

      const isNewMinimum =
        minAmountStr === undefined ||
        currentAmount < Number.parseFloat(minAmountStr);

      return isNewMinimum ? currentItem.price.amount : minAmountStr;
    },
    undefined as string | undefined,
  );
};

function hasBaggageDiscount(baggage: ExtendedBaggage): boolean {
  const modifiers = baggage.pricing?.modifiers;

  return Boolean(
    modifiers && typeof modifiers === 'object' && Object.keys(modifiers).length,
  );
}

function getBaggageOriginalPrice(baggage: ExtendedBaggage): string | undefined {
  const originalPrice = baggage.pricing?.total;

  if (typeof originalPrice !== 'string') {
    return undefined;
  }

  return Number.isNaN(Number.parseFloat(originalPrice))
    ? undefined
    : originalPrice;
}

const getBaggageDiscountMinPrices = (
  baggages: ExtendedBaggage[],
): {
  discountedMinPrice?: string;
  originalMinPrice?: string;
} => {
  const discountedBaggages = baggages.filter(hasBaggageDiscount);

  if (!discountedBaggages.length) {
    return {};
  }

  const discountedMinPrice = getBaggageMinPrice(discountedBaggages);
  const originalMinPrice = discountedBaggages.reduce(
    (minAmountStr, currentItem) => {
      const currentAmount = Number.parseFloat(
        getBaggageOriginalPrice(currentItem) || '',
      );

      if (Number.isNaN(currentAmount)) {
        return minAmountStr;
      }

      const isNewMinimum =
        minAmountStr === undefined ||
        currentAmount < Number.parseFloat(minAmountStr);

      return isNewMinimum ? String(currentAmount) : minAmountStr;
    },
    undefined as string | undefined,
  );

  return { discountedMinPrice, originalMinPrice };
};

export const priceLib = {
  getSelectedBaggagePrice,
  getSelectedBaggageOriginalPrice,
  getTotalPrice,
  getTotalOriginalPrice,
  getBaggageMinPrice,
  hasBaggageDiscount,
  getBaggageOriginalPrice,
  getBaggageDiscountMinPrices,
};
